import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthFetch } from '../lib/useAuthFetch'
import { OperatorTypeFilter, type OperatorType } from '../components/filters/OperatorTypeFilter'
import { trackStateToFlags, type DispensaryFeatureProps } from '../components/map/types'
import { TRACK_STATE_COLORS, BUSINESS_TYPE_COLORS, trackBlockColor } from '../components/map/mapStyle'
import {
  DispensaryMap,
  type DispensaryMapHandle,
  type MapOverlaySpec,
  type PopupCompetitor,
} from '../components/map/DispensaryMap'
import type { MapLayerSpec } from '../components/map/layers'
import { MapLegend } from '../components/map/MapLegend'
import type { DispensaryFeatureCollection } from '../hooks/useDispensaryMap'
import { isOwnedRivalCandidate } from '../components/map/ownLocationMatch'

const API = import.meta.env.VITE_API_URL ?? ''

function StepBar({ active }: { active: number }) {
  const steps = [
    { n: '01', label: 'Org Setup', sub: 'Complete' },
    { n: '02', label: 'Add Locations', sub: 'Complete' },
    { n: '03', label: 'Find Rivals', sub: 'Track rivals' },
  ]
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', padding: '14px 28px', display: 'flex', alignItems: 'center', boxShadow: 'var(--card-shadow)', flexShrink: 0 }}>
      {steps.map((s, i) => {
        const isDone = i < active
        const isActive = i === active
        return (
          <div key={s.n} style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, position: 'relative' }}>
            <div style={{ width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 500, flexShrink: 0, background: isDone ? 'var(--accent)' : isActive ? 'var(--accent)' : 'var(--surface-3)', color: isDone || isActive ? '#fff' : 'var(--text-3)', boxShadow: isActive ? '0 0 0 4px rgba(9,161,161,0.15)' : isDone ? '0 0 0 3px rgba(9,161,161,0.10)' : 'none' }}>
              {isDone ? <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" style={{ width: 13, height: 13 }}><polyline points="20 6 9 17 4 12" /></svg> : s.n}
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: isActive || isDone ? 'var(--text-1)' : 'var(--text-3)', whiteSpace: 'nowrap' as const }}>{s.label}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', whiteSpace: 'nowrap' as const }}>{s.sub}</div>
            </div>
            {i < steps.length - 1 && <div style={{ position: 'absolute', left: 145, width: 'calc(100% - 150px)', height: 2, background: isDone ? 'var(--accent)' : 'var(--border-2)', top: '50%', transform: 'translateY(-50%)' }} />}
          </div>
        )
      })}
    </div>
  )
}

type SortMode = 'distance' | 'name' | 'status'

// California initial view
const CA_VIEWPORT = { longitude: -119.4179, latitude: 36.7783, zoom: 5.5 }

interface Location {
  id: string
  name: string
  address?: string
  lat?: number | null
  lng?: number | null
}

/** A sidebar row — the dispensary FEATURE itself plus display extras.
 *  Track/block state lives ONLY on `props.track_state` (the applied map
 *  FC), so the row dot, buttons, pin inner-dot, and popup badge all read
 *  the same object and cannot disagree. */
interface SidebarItem {
  props: DispensaryFeatureProps
  address: string
  distance_miles: number
  lat: number
  lng: number
}

// Build a GeoJSON circle polygon (approximate) for the radius ring
function makeCircleGeoJSON(lat: number, lng: number, radiusMiles: number) {
  const radiusKm = radiusMiles * 1.60934
  const points = 64
  const coords: [number, number][] = []
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * 2 * Math.PI
    const dx = (radiusKm / 111.32) * Math.cos(angle)
    const dy = (radiusKm / (111.32 * Math.cos((lat * Math.PI) / 180))) * Math.sin(angle)
    coords.push([lng + dy, lat + dx])
  }
  coords.push(coords[0])
  return {
    type: 'FeatureCollection' as const,
    features: [{
      type: 'Feature' as const,
      geometry: { type: 'Polygon' as const, coordinates: [coords] },
      properties: {},
    }],
  }
}

const radiusFillLayer: MapLayerSpec = {
  id: 'radius-fill',
  type: 'fill',
  paint: { 'fill-color': '#1d9e75', 'fill-opacity': 0.12 },
}

const radiusOutlineLayer: MapLayerSpec = {
  id: 'radius-outline',
  type: 'line',
  paint: { 'line-color': '#1d9e75', 'line-width': 3, 'line-opacity': 1, 'line-dasharray': [5, 3] },
}

export default function CompetitorDiscovery() {
  const authFetch = useAuthFetch()
  const navigate = useNavigate()
  const mapRef            = useRef<DispensaryMapHandle | null>(null)
  // Bumped once per live map INSTANCE (StrictMode pass-2 map included), at
  // the first of styledata/idle/load — NOT only 'load', which waits on every
  // initial tile and stalls for 30s+ on a throttled Mapbox token. The fly-to
  // effect below keys on this + selectedLocation, so the camera centers no
  // matter which arrives last or how slowly the style/tiles load.
  const [mapLoadCount, setMapLoadCount] = useState(0)

  const [locations, setLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null)
  const [loading, setLoading] = useState(false)
  const [dispensariesLoading, setDispensariesLoading] = useState(true)
  const [scanned, setScanned] = useState(false)
  const [mapMoved, setMapMoved] = useState(false)
  const [operatorType, setOperatorType] = useState<OperatorType>('both')
  const [sortMode, setSortMode] = useState<SortMode>('distance')
  const [radius, setRadius] = useState(5)
  // The applied map FC (server truth + optimistic patches) pushed up by
  // DispensaryMap — the ONLY state source for sidebar rows.
  const [dispensaries, setDispensaries] = useState<DispensaryFeatureCollection>({ type: 'FeatureCollection', features: [] })
  // Canonical org-wide totals — same endpoint CommandCenter's pills use.
  const [summary, setSummary] = useState<{ tracked: number; blocked: number } | null>(null)
  // Merged competitor slots across all org locations, lifted from the map's
  // single internal fetch (RC2) — powers the slot-manager modal (A4).
  const [competitors, setCompetitors] = useState<PopupCompetitor[]>([])
  const [slotManagerOpen, setSlotManagerOpen] = useState(false)

  useEffect(() => {
    authFetch(`${API}/api/v1/locations`)
      .then((r) => r.json())
      .then((data) => {
        setLocations(data.locations || [])
        if (data.locations?.length > 0) setSelectedLocation(data.locations[0])
      })
      .catch(() => {})
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSummary = useCallback(() => {
    authFetch(`${API}/api/v1/competitors/summary`)
      .then(r => r.json())
      .then(d => {
        const s = d.data ?? d
        if (typeof s?.tracked === 'number') setSummary({ tracked: s.tracked, blocked: s.blocked })
      })
      .catch(() => {})
  }, [authFetch])

  useEffect(() => { fetchSummary() }, [fetchSummary])

  const handleMapLoad = useCallback(() => { setMapLoadCount(n => n + 1) }, [])

  // Center on the selected location once BOTH exist: a live map instance
  // (mapLoadCount > 0, re-bumped per instance) and a location with
  // coordinates. Re-runs on either dependency PLUS the first dispensary-data
  // arrival, so no ordering or load speed can drop it. Exactly one fly per
  // (map instance, location) — the key guard keeps the extra retrigger from
  // yanking the camera back after the user pans away.
  const flownKeyRef = useRef('')
  const hasDispensaryData = dispensaries.features.length > 0
  useEffect(() => {
    if (mapLoadCount === 0) return
    const loc = selectedLocation
    if (!loc?.lat || !loc?.lng) return
    const key = `${mapLoadCount}:${loc.id}`
    if (flownKeyRef.current === key) return
    // No live map object (e.g. the StrictMode teardown window) — leave the
    // key unrecorded so the next signal (data arrival, instance bump) retries.
    if (!mapRef.current?.getMap()) return
    flownKeyRef.current = key
    mapRef.current.flyTo({ center: [Number(loc.lng), Number(loc.lat)], zoom: 12, duration: 800 })
  }, [mapLoadCount, selectedLocation, hasDispensaryData])

  const handleMoveEnd = useCallback(() => { setMapMoved(true) }, [])

  const handleDispensariesChange = useCallback((fc: DispensaryFeatureCollection, isLoading: boolean) => {
    setDispensaries(fc)
    setDispensariesLoading(isLoading)
  }, [])

  const handleDiscover = async () => {
    if (!selectedLocation?.id) return
    setLoading(true)
    setMapMoved(false)
    try {
      // Refresh radius results server-side; the sidebar itself derives from
      // the live dispensary GeoJSON, so the response needs no local copy.
      await authFetch(`${API}/api/v1/locations/${selectedLocation.id}/discover?radius=${radius}`)
    } catch { /* sidebar still renders from map data */ } finally {
      setLoading(false)
      setScanned(true)
    }
  }

  // Sidebar Track/Block — same single action path the map popup uses
  // (DispensaryMap.requestToggle): opens the billing confirm dialog, then on
  // confirm runs the one resolver / optimistic patch / refetch. Pin dot, row
  // dot, popup badge, and buttons move together.
  const handleToggle = useCallback((item: SidebarItem, action: 'track' | 'block') => {
    const locId = selectedLocation?.id
    const map = mapRef.current
    if (!locId || !map) return
    map.requestToggle(item.props, action, locId)
  }, [selectedLocation?.id])

  const handleLaunch = () => {
    navigate('/today')
  }

  const centerLat = selectedLocation?.lat ? Number(selectedLocation.lat) : null
  const centerLng = selectedLocation?.lng ? Number(selectedLocation.lng) : null
  const radiusGeoJSON = useMemo(
    () => (centerLat && centerLng ? makeCircleGeoJSON(centerLat, centerLng, radius) : null),
    [centerLat, centerLng, radius],
  )

  // Radius ring overlay — hosted by DispensaryMap. Own-location markers are
  // rendered by DispensaryMap for every location in the `locations` prop
  // (Phase 2, shared layer); the selected store (actionLocationId) is
  // emphasized. No per-page own-location marker building here.
  const mapOverlays = useMemo<MapOverlaySpec[]>(
    () => radiusGeoJSON
      ? [{ id: 'radius', data: radiusGeoJSON as unknown as GeoJSON.FeatureCollection, layers: [radiusFillLayer, radiusOutlineLayer] }]
      : [],
    [radiusGeoJSON],
  )

  // Sidebar items derived from the live (applied) dispensary GeoJSON within
  // radius — no scan required, and track/block state rides along on props.
  const sidebarItems = useMemo<SidebarItem[]>(() => {
    if (!dispensaries?.features?.length || !centerLat || !centerLng) return []
    const R = 6371
    const seen = new Set<string>()
    return dispensaries.features
      .filter(f => {
        if (!f.geometry || (f.geometry as any).type !== 'Point') return false
        const [lng, lat] = (f.geometry as any).coordinates as [number, number]
        return !isOwnedRivalCandidate(f.properties?.is_own, lng, lat, locations)
      })
      .filter(f => {
        const bt = (f.properties as any)?.business_type
        if (operatorType === 'storefront') return bt === 'storefront' || bt === 'both' || !bt
        if (operatorType === 'delivery') return bt === 'delivery' || bt === 'both'
        return true
      })
      .map(f => {
        const p = f.properties as DispensaryFeatureProps
        const [lng, lat] = (f.geometry as any).coordinates as [number, number]
        const dLat = (lat - centerLat) * Math.PI / 180
        const dLng = (lng - centerLng) * Math.PI / 180
        const a = Math.sin(dLat / 2) ** 2 + Math.cos(centerLat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2
        const distMiles = (R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))) / 1.60934
        return {
          props: p,
          address: `${p.city ?? ''}${p.county ? `, ${p.county} Co.` : ''}, CA`,
          distance_miles: Math.round(distMiles * 10) / 10,
          lat,
          lng,
        }
      })
      .filter(item => {
        if (seen.has(item.props.id)) return false
        seen.add(item.props.id)
        return true
      })
      .sort((a, b) => a.distance_miles - b.distance_miles)
      .slice(0, 150)
  }, [dispensaries, centerLat, centerLng, radius, operatorType, locations])

  // Selected items always float to top; within each group sort by sortMode
  const sortedSidebarItems = useMemo(() => {
    return [...sidebarItems].sort((a, b) => {
      const af = trackStateToFlags(a.props.track_state)
      const bf = trackStateToFlags(b.props.track_state)
      const aActive = af.isTracked || af.isBlocked
      const bActive = bf.isTracked || bf.isBlocked
      if (aActive !== bActive) return aActive ? -1 : 1
      if (sortMode === 'name') return a.props.name.localeCompare(b.props.name)
      if (sortMode === 'status') {
        const aScore = af.isBlocked ? 2 : af.isTracked ? 1 : 0
        const bScore = bf.isBlocked ? 2 : bf.isTracked ? 1 : 0
        if (aScore !== bScore) return bScore - aScore
      }
      return a.distance_miles - b.distance_miles
    })
  }, [sidebarItems, sortMode])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg)', fontFamily: 'var(--sans)' }}>
      {/* Topbar */}
      <div style={{ padding: '10px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>Find your rivals</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.08em', marginTop: 2 }}>SCREEN 03 · RIVAL DISCOVERY · STEP 3 OF 3</div>
        </div>
      </div>

      {/* Step bar */}
      <div style={{ padding: '10px 20px 0', flexShrink: 0 }}>
        <StepBar active={2} />
      </div>

    <div style={{ display: 'flex', flex: 1, minHeight: 0, padding: '0 20px 16px' }}>

      {/* ── Map panel ── */}
      <div style={{
        flex: '0 0 68%',
        position: 'relative',
        borderRadius: '8px 0 0 8px',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        borderRight: 'none',
      }}>
        <DispensaryMap
          mapRef={mapRef}
          authFetch={authFetch}
          filters={{ type: operatorType === 'both' ? undefined : operatorType }}
          showStyleToggle
          initialViewState={CA_VIEWPORT}
          locations={locations}
          actionLocationId={selectedLocation?.id}
          onPopupAction={fetchSummary}
          onCompetitorsChange={setCompetitors}
          onMapLoad={handleMapLoad}
          onMoveEnd={handleMoveEnd}
          onDispensariesChange={handleDispensariesChange}
          overlays={mapOverlays}
          style={{ width: '100%', height: '100%' }}
        >
          {/* Redo search overlay — appears after map pan when a scan has been run */}
          {mapMoved && scanned && (
            <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
              <button
                onClick={() => handleDiscover()}
                style={{ padding: '7px 16px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: 'var(--surface)', border: '1.5px solid var(--border)', color: 'var(--text-1)', cursor: 'pointer', fontFamily: 'var(--sans)', boxShadow: '0 2px 12px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}
              >
                Redo search in this area
              </button>
            </div>
          )}

          <MapLegend showAll />
        </DispensaryMap>
      </div>

      {/* ── Right panel ── */}
      <div style={{
        flex: '0 0 32%', display: 'flex', flexDirection: 'column',
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: '0 8px 8px 0', overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-1)', marginBottom: 6 }}>
            Identify your rivals
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.55 }}>
            Track rivals to monitor their prices and promotions.
          </div>

          <div style={{ marginTop: 12 }}>
            <OperatorTypeFilter value={operatorType} onChange={setOperatorType} />
          </div>

          {/* Radius slider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>RADIUS</span>
            <input
              type="range"
              min={1} max={25} step={1}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              style={{ flex: 1, accentColor: '#1d9e75', cursor: 'pointer' }}
            />
            <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-2)', minWidth: 36, textAlign: 'right' }}>
              {radius} mi
            </span>
          </div>

          {/* Sort controls */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 10 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.06em', marginRight: 2 }}>SORT</span>
            {(['distance', 'name', 'status'] as SortMode[]).map(mode => (
              <button
                key={mode}
                onClick={() => setSortMode(mode)}
                style={{
                  fontSize: 9, padding: '3px 8px', borderRadius: 4, cursor: 'pointer',
                  fontFamily: 'var(--mono)', fontWeight: 600, letterSpacing: '0.04em',
                  border: `1px solid ${sortMode === mode ? 'var(--accent)' : 'var(--border-2)'}`,
                  background: sortMode === mode ? 'rgba(29,158,117,0.12)' : 'transparent',
                  color: sortMode === mode ? 'var(--accent)' : 'var(--text-3)',
                  textTransform: 'uppercase' as const,
                  transition: 'all 0.12s',
                }}
              >
                {mode}
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            {locations.length >= 1 && (
              <select
                value={selectedLocation?.id || ''}
                onChange={(e) => {
                  const loc = locations.find((l) => l.id === e.target.value) || null
                  setSelectedLocation(loc)
                }}
                style={{
                  flex: 1, background: 'var(--surface-2)',
                  border: '1.5px solid var(--border-2)', borderRadius: 6,
                  padding: '7px 10px', color: 'var(--text-1)',
                  fontSize: 12, fontFamily: 'var(--sans)', outline: 'none', cursor: 'pointer',
                }}
              >
                {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
            <button
              className="btn btn-ghost"
              style={{ fontSize: 12, padding: '7px 14px', whiteSpace: 'nowrap', flexShrink: 0 }}
              onClick={handleDiscover}
              disabled={loading || !selectedLocation?.id}
            >
              {loading ? 'Scanning...' : 'Scan market'}
            </button>
          </div>
        </div>

        {/* Competitor list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          {sidebarItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 16px' }}>
              <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.65, maxWidth: 280, margin: '0 auto' }}>
                {loading || dispensariesLoading
                  ? 'Loading dispensaries in your area...'
                  : !selectedLocation
                    ? 'Select a location above to see nearby rivals.'
                    : 'No dispensaries found within your radius. Try increasing it.'}
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {sortedSidebarItems.map((item) => {
                // ONE source: the feature's track_state — identical to the
                // pin inner-dot, popup badge, and popup buttons.
                const { isTracked, isBlocked } = trackStateToFlags(item.props.track_state)
                const stateColor = trackBlockColor({ isTracked, isBlocked })
                const isDeliveryItem = item.props.business_type === 'delivery' || item.props.business_type === 'both'
                const businessColor = isDeliveryItem ? BUSINESS_TYPE_COLORS.delivery : BUSINESS_TYPE_COLORS.storefront
                const rowBg = stateColor ? `${stateColor}12` : 'var(--surface-2)'
                const rowBorder = stateColor ? `${stateColor}4d` : 'var(--border)'
                return (
                  <div key={item.props.id} style={{
                    background: rowBg,
                    border: `1px solid ${rowBorder}`,
                    borderRadius: 6, padding: '10px 12px',
                    display: 'flex', alignItems: 'center', gap: 10,
                    transition: 'border-color 0.1s, background 0.1s',
                  }}>
                    {stateColor ? (
                      <span style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: stateColor, display: 'inline-block' }} />
                    ) : (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: businessColor, opacity: 0.5, display: 'inline-block' }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 500, color: 'var(--text-1)', marginBottom: 2,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>{item.props.name}</div>
                      <div style={{
                        fontSize: 10, color: 'var(--text-3)', fontFamily: 'Space Mono, monospace',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {`${item.distance_miles.toFixed(1)} mi`}
                        {item.address ? '  ·  ' : ''}
                        {item.address}
                      </div>
                      <div style={{
                        fontSize: 10, color: 'var(--text-2)', fontFamily: 'Space Mono, monospace',
                        marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        Track rivals · Included in this location’s plan
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                      <button
                        style={{
                          fontSize: 11, padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                          fontFamily: 'var(--sans)', fontWeight: 500,
                          border: `1px solid ${isTracked ? TRACK_STATE_COLORS.tracking : 'var(--border-2)'}`,
                          background: isTracked ? TRACK_STATE_COLORS.tracking : 'transparent',
                          color: isTracked ? '#fff' : 'var(--text-2)',
                        }}
                        onClick={() => handleToggle(item, 'track')}
                      >{isTracked ? 'Untrack' : 'Track'}</button>

                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 16px', borderTop: '1px solid var(--border)',
          background: 'var(--surface-2)', display: 'flex',
          alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, gap: 12,
        }}>
          {((summary?.tracked ?? 0) > 0) ? (
            <button
              onClick={() => setSlotManagerOpen(true)}
              title="Manage tracked rivals"
              data-testid="discovery-slot-summary"
              style={{
                fontSize: 12, minWidth: 0, flex: 1, textAlign: 'left',
                background: 'transparent', border: 'none', cursor: 'pointer',
                padding: 0, fontFamily: 'var(--sans)', color: 'var(--text-1)',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {(summary?.tracked ?? 0) > 0 && <span style={{ color: 'var(--accent)' }}>{summary!.tracked} tracking</span>}
                <span style={{ color: 'var(--text-3)' }}>· Included in the location plan</span>
                {locations.length > 1 && <span style={{ color: 'var(--text-3)', fontSize: 10, fontFamily: 'Space Mono, monospace' }}>across {locations.length} locations</span>}
                <span style={{ color: 'var(--accent)', fontSize: 10, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 2, whiteSpace: 'nowrap' }}>Manage</span>
              </span>
            </button>
          ) : (
            <div style={{ fontSize: 12, minWidth: 0 }}>
              <span style={{ color: 'var(--text-3)', fontSize: 11, fontFamily: 'Space Mono, monospace' }}>
                No rivals selected
              </span>
            </div>
          )}
          <button
            className="btn btn-primary"
            style={{ fontSize: 12, padding: '7px 16px', flexShrink: 0 }}
            onClick={handleLaunch}
          >
            {(summary?.tracked ?? 0) > 0 ? 'Go to Today' : 'Skip for now'}
          </button>
        </div>
      </div>
    </div>

      {slotManagerOpen && (
        <SlotManagerModal
          competitors={competitors}
          locations={locations}
          onClose={() => setSlotManagerOpen(false)}
          onConfirm={async (targets) => {
            await mapRef.current?.removeSlots(targets)
            setSlotManagerOpen(false)
            fetchSummary()
          }}
        />
      )}
    </div>
  )
}

// ── Rival-manager modal ────────────────────────────────────────────────────
// Lists every tracked and blocked relationship grouped by location.
// Applies removals on Confirm through DispensaryMap.removeSlots (the existing
// DELETE endpoint) — no new mutation path. Close discards.

interface SlotRow {
  key: string
  competitorId: string
  name: string
  locationId: string
  locationName: string
  slotType: 'track' | 'block'
}

function SlotManagerModal({
  competitors,
  locations,
  onClose,
  onConfirm,
}: {
  competitors: PopupCompetitor[]
  locations: Location[]
  onClose: () => void
  onConfirm: (targets: { locationId: string; competitorId: string; slotType: 'track' | 'block' }[]) => Promise<void> | void
}) {
  const [removed, setRemoved] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)

  const locName = useCallback(
    (id?: string) => locations.find((l) => l.id === id)?.name ?? 'Unknown location',
    [locations],
  )

  const rows = useMemo<SlotRow[]>(() => {
    const flat: { competitorId: string; name: string; locationId: string; slotType: 'track' | 'block' }[] = []
    for (const c of competitors) {
      const slots = c.slots ?? (c.slot_type && c.location_id ? [{ location_id: c.location_id, slot_type: c.slot_type }] : [])
      for (const s of slots) {
        if (s.slot_type !== 'track' || !s.location_id) continue
        flat.push({ competitorId: c.competitor_id, name: c.name, locationId: s.location_id, slotType: s.slot_type })
      }
    }
    return flat.map(f => {
      return {
        key: `${f.competitorId}:${f.locationId}:${f.slotType}`,
        competitorId: f.competitorId,
        name: f.name,
        locationId: f.locationId,
        locationName: locName(f.locationId),
        slotType: f.slotType,
      }
    })
  }, [competitors, locName])

  // Group rows by location for the grouped feed.
  const grouped = useMemo(() => {
    const map = new Map<string, { locationName: string; rows: SlotRow[] }>()
    for (const r of rows) {
      const g = map.get(r.locationId) ?? { locationName: r.locationName, rows: [] }
      g.rows.push(r)
      map.set(r.locationId, g)
    }
    return [...map.values()].sort((a, b) => a.locationName.localeCompare(b.locationName))
  }, [rows])

  const toggle = (key: string) => {
    setRemoved((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleConfirm = async () => {
    const targets = [...removed]
      .map((key) => rows.find((r) => r.key === key))
      .filter((r): r is SlotRow => Boolean(r))
      .map((r) => ({ locationId: r.locationId, competitorId: r.competitorId, slotType: r.slotType }))
    if (!targets.length) return
    setBusy(true)
    try {
      await onConfirm(targets)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      onClick={busy ? undefined : onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(1.5px)',
        fontFamily: 'var(--sans)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="discovery-slot-manager"
        style={{
          width: 460, maxWidth: '92%', maxHeight: '82vh', display: 'flex', flexDirection: 'column',
          background: 'var(--surface-solid, var(--surface))', border: '1px solid var(--border)',
          borderRadius: 10, boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px 12px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-1)' }}>Manage rivals</div>
          <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>
            Remove tracked rivals. Nothing changes until you confirm.
          </div>
        </div>

        {/* Grouped feed */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', minHeight: 0 }}>
          {grouped.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 12px', fontSize: 12, color: 'var(--text-3)' }}>
              No active rivals to manage.
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.locationName} style={{ marginBottom: 14 }}>
                <div style={{
                  fontFamily: 'var(--mono)', fontSize: 9.5, letterSpacing: '0.08em', textTransform: 'uppercase',
                  color: 'var(--text-3)', marginBottom: 6,
                }}>
                  {g.locationName}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {g.rows.map((r) => {
                    const isRemoved = removed.has(r.key)
                    const color = r.slotType === 'block' ? TRACK_STATE_COLORS.blocked : TRACK_STATE_COLORS.tracking
                    return (
                      <div key={r.key} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 10px', borderRadius: 6,
                        border: `1px solid ${isRemoved ? 'var(--border)' : `${color}4d`}`,
                        background: isRemoved ? 'transparent' : `${color}12`,
                        opacity: isRemoved ? 0.5 : 1,
                      }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: color }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontSize: 13, fontWeight: 500, color: 'var(--text-1)',
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            textDecoration: isRemoved ? 'line-through' : 'none',
                          }}>{r.name}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>
                            Tracking · Included in this location’s plan
                          </div>
                        </div>
                        <button
                          onClick={() => toggle(r.key)}
                          disabled={busy}
                          style={{
                            flexShrink: 0, fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 4,
                            cursor: busy ? 'default' : 'pointer', fontFamily: 'var(--sans)',
                            border: `1px solid ${isRemoved ? 'var(--accent)' : 'var(--border-2)'}`,
                            background: isRemoved ? 'var(--accent-soft)' : 'transparent',
                            color: isRemoved ? 'var(--accent)' : 'var(--text-2)',
                          }}
                        >
                          {isRemoved ? 'Undo' : 'Remove'}
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 16px 14px', borderTop: '1px solid var(--border)', flexShrink: 0 }}>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 10 }}>
            Removing a rival does not change the selected location plan or monthly price.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={onClose}
              disabled={busy}
              style={{
                flex: '0 0 auto', padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                fontFamily: 'var(--sans)', cursor: busy ? 'default' : 'pointer',
                border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)',
              }}
            >
              Close
            </button>
            <button
              onClick={handleConfirm}
              disabled={busy || removed.size === 0}
              style={{
                flex: 1, padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                fontFamily: 'var(--sans)', cursor: busy || removed.size === 0 ? 'default' : 'pointer',
                border: '1px solid var(--accent)', background: 'var(--accent)', color: 'var(--cta-fg-on-pos, #fff)',
                opacity: busy || removed.size === 0 ? 0.55 : 1,
              }}
            >
              {busy ? 'Applying…' : removed.size > 0 ? `Confirm — remove ${removed.size}` : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
